import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Box,
  Typography,
  Divider,
  Chip,
  Stack,
  Paper,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import {
  WarningAmber,
  Insights,
  Inventory2,
  LocalOffer,
  Refresh,
  Close,
  Assignment,
  QueryStats,
  ShoppingBag,
  OpenInNew,
} from "@mui/icons-material";
import { fetchWithToken } from "../api/apiutils";

const SATU_API_BASE = "https://satu-analyzer.onrender.com/analyze";

// Палитра из UI_SPEC_ADMIN_MARKETPLACE_PLAN_MODAL
const TONES = {
  green: { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" },
  amber: { bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
  red: { bg: "#fff1f2", color: "#be123c", border: "#fecdd3" },
  sky: { bg: "#f0f9ff", color: "#0369a1", border: "#bae6fd" },
  slate: { bg: "#f1f5f9", color: "#334155", border: "#e2e8f0" },
  darkSlate: { bg: "#e2e8f0", color: "#1e293b", border: "#cbd5e1" },
  purple: { bg: "#faf5ff", color: "#7e22ce", border: "#e9d5ff" },
  orange: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
};

const DATA_QUALITY = {
  ok: { label: "Отличное качество", tone: "green" },
  low: { label: "Мало данных", tone: "amber" },
  none: { label: "Нет совпадений", tone: "slate" },
  suspect: { label: "Подозрительная цена", tone: "red" },
};

const QUERY_SOURCE = {
  marketplace_plan: "по плану ТС",
  summary_query: "по ТЗ",
  lot_name: "по названию лота",
};

const OFFER_STATUS = {
  match: { label: "✓ Подходит под ТС", tone: "green", order: 0 },
  overspec: { label: "Превышает ТС", tone: "sky", order: 1 },
  other_pack: { label: "Другая фасовка", tone: "orange", order: 2 },
  other_variant: { label: "Другой вариант", tone: "purple", order: 3 },
  outlier: { label: "Ценовой выброс", tone: "darkSlate", order: 4 },
  excluded: { label: "✕ Исключён", tone: "red", order: 5 },
};

const confidenceInfo = (confidence) => {
  if (confidence === null || confidence === undefined)
    return { label: "-", tone: "slate" };
  const pct = Math.round(confidence * 100);
  if (pct >= 80) return { label: `Высокая (${pct}%)`, tone: "green" };
  if (pct >= 50) return { label: `Средняя (${pct}%)`, tone: "amber" };
  return { label: `Низкая (${pct}%)`, tone: "red" };
};

const formatPrice = (price) => {
  if (price === null || price === undefined || Number.isNaN(price)) return "-";
  return `${new Intl.NumberFormat("ru-RU").format(Math.round(price))} ₸`;
};

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString("ru-RU", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "-";

const formatAge = (hours) => {
  if (hours === null || hours === undefined) return "";
  if (hours < 1) return "менее часа назад";
  if (hours < 24) return `${Math.round(hours)} ч. назад`;
  return `${Math.floor(hours / 24)} дн. назад`;
};

const scoreColor = (score) =>
  score >= 70 ? "success" : score >= 50 ? "warning" : "default";

const buildSatuParams = (query, endPage, strictTitleMatch, bestLimit) =>
  new URLSearchParams({
    query,
    selected_source: "satu",
    selected_category: "auto",
    start_page: "1",
    end_page: String(endPage),
    remove_outliers: "false",
    strict_title_match: String(strictTitleMatch),
    best_limit: String(bestLimit),
  });

const readApiError = async (res) => {
  try {
    const body = await res.json();
    return body?.Message || body?.message || `Ошибка ${res.status}`;
  } catch {
    return `Ошибка ${res.status}`;
  }
};

const ToneChip = ({ tone, label, sx }) => {
  const t = TONES[tone] || TONES.slate;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        bgcolor: t.bg,
        color: t.color,
        border: `1px solid ${t.border}`,
        fontWeight: 600,
        height: "auto",
        "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 },
        ...sx,
      }}
    />
  );
};

const StatCard = ({ label, value, color }) => (
  <Paper
    elevation={0}
    className="rounded-xl border border-slate-200 bg-white p-3"
  >
    <Typography
      sx={{
        fontSize: 11,
        color: "#94a3b8",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontWeight: 700, fontSize: 18, color: color || "#0f172a" }}
    >
      {value}
    </Typography>
  </Paper>
);

const SectionShell = ({
  icon,
  iconClass,
  iconColor,
  title,
  subtitle,
  children,
}) => (
  <Box
    className="rounded-3xl border border-slate-200 bg-slate-50 p-6"
    sx={{ boxShadow: "0 4px 20px rgba(0,0,0,0.04)", minWidth: 0 }}
  >
    <Box className="mb-5 flex items-center gap-3">
      <Box
        className={`flex h-12 w-12 items-center justify-center rounded-2xl ${iconClass}`}
        sx={{ color: iconColor }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 22, color: "#0f172a" }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ color: "#64748b", fontSize: 14 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
    <Divider sx={{ mb: 3 }} />
    {children}
  </Box>
);

const InfoCard = ({ title, children }) => (
  <Paper elevation={0} className="rounded-2xl border border-slate-200 p-5">
    {title && (
      <Typography sx={{ fontWeight: 700, color: "#334155", mb: 2 }}>
        {title}
      </Typography>
    )}
    {children}
  </Paper>
);

const Label = ({ children }) => (
  <Typography sx={{ fontSize: 13, color: "#64748b", mb: 1 }}>
    {children}
  </Typography>
);

const ChipGroup = ({ label, items, tone, prefix = "" }) => (
  <Box>
    <Label>{label}</Label>
    {items?.length ? (
      <Box className="flex flex-wrap gap-2">
        {items.map((item, i) => (
          <ToneChip key={i} tone={tone} label={`${prefix}${item}`} />
        ))}
      </Box>
    ) : (
      <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>Нет</Typography>
    )}
  </Box>
);

const ParamRow = ({ label, children }) => (
  <Box className="flex items-center justify-between gap-4 rounded-xl bg-slate-50 p-3">
    <Typography sx={{ fontWeight: 600, color: "#334155" }}>{label}</Typography>
    <Box sx={{ textAlign: "right", color: "#0f172a" }}>{children}</Box>
  </Box>
);

const EmptyBox = ({ children, action }) => (
  <Box className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-10 text-center">
    <Typography sx={{ color: "#64748b", fontWeight: 600 }}>
      {children}
    </Typography>
    {action}
  </Box>
);

// ── Левая колонка: план поиска по ТС ──
const PlanColumn = ({ plan }) => {
  if (!plan) {
    return <EmptyBox>План поиска по ТС ещё не сформирован</EmptyBox>;
  }

  if (!plan.isMarketplaceItem) {
    return (
      <InfoCard>
        <ParamRow label="Товарный статус">
          <ToneChip tone="slate" label="Услуга / Работа / Нетоварный лот" />
        </ParamRow>
      </InfoCard>
    );
  }

  const confidence = confidenceInfo(plan.confidence);
  const pack =
    plan.packValue !== null && plan.packValue !== undefined
      ? `${plan.packValue} ${plan.packUnit || ""}`.trim()
      : "Не указана (1 шт)";

  return (
    <Stack spacing={3}>
      <InfoCard title="Поисковые запросы">
        <Label>Основной запрос</Label>
        <Box className="mb-4 rounded-xl bg-slate-100 px-4 py-3">
          <Typography
            sx={{ fontWeight: 600, color: "#0f172a", wordBreak: "break-word" }}
          >
            {plan.query || "-"}
          </Typography>
        </Box>
        <ChipGroup
          label="Запасные варианты (если основной запрос пуст):"
          items={plan.fallbackQueries}
          tone="slate"
        />
      </InfoCard>

      <InfoCard title="Правила отбора">
        <Stack spacing={2.5}>
          <ChipGroup
            label="Обязательные слова (должны быть в названии)"
            items={plan.requiredWords?.map((group) => group.join(" / "))}
            tone="green"
            prefix="+ "
          />
          <ChipGroup
            label="Стоп-слова (сразу отсекать товар)"
            items={plan.excludeWords}
            tone="red"
            prefix="✕ "
          />
          <ChipGroup
            label="Слова завышения (более дорогой класс)"
            items={plan.overspecWords}
            tone="amber"
          />
          <ChipGroup
            label="Конфликтующие свойства"
            items={plan.variantConflicts}
            tone="purple"
          />
        </Stack>
      </InfoCard>

      <InfoCard title="Параметры изделия">
        <Stack spacing={1.5}>
          <ParamRow label="Опорное существительное">
            {plan.headNouns?.length ? plan.headNouns.join(", ") : "-"}
          </ParamRow>
          <ParamRow label="Фасовка (упаковка)">{pack}</ParamRow>
          <ParamRow label="Тип единицы закупки">
            {plan.lotUnitType || "-"}
          </ParamRow>
          <ParamRow label="Товарный статус">
            <ToneChip tone="green" label="Товарный лот" />
          </ParamRow>
          <ParamRow label="Уверенность ИИ">
            <ToneChip tone={confidence.tone} label={confidence.label} />
          </ParamRow>
        </Stack>
      </InfoCard>
    </Stack>
  );
};

// ── Правая колонка: анализ цен и кэш Satu.kz ──
const CacheColumn = ({ plan, cache, refreshing, onRefresh }) => {
  if (plan && !plan.isMarketplaceItem) {
    return (
      <EmptyBox>
        Сбор цен с маркетплейсов не выполняется для нетоварных лотов
      </EmptyBox>
    );
  }

  if (!cache) {
    return (
      <EmptyBox
        action={
          <Button
            variant="contained"
            size="small"
            startIcon={
              refreshing ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <Refresh fontSize="small" />
              )
            }
            onClick={onRefresh}
            disabled={refreshing}
          >
            Собрать цены сейчас
          </Button>
        }
      >
        Цены ещё не запрашивались
      </EmptyBox>
    );
  }

  const quality = DATA_QUALITY[cache.dataQuality] || {
    label: cache.dataQuality || "-",
    tone: "slate",
  };
  const source = QUERY_SOURCE[cache.querySource];

  return (
    <Stack spacing={3}>
      <InfoCard title="Статус и свежесть данных">
        <Stack spacing={1.5}>
          <ParamRow label="Качество данных">
            <ToneChip tone={quality.tone} label={quality.label} />
          </ParamRow>
          <ParamRow label="Использованный запрос">
            <Typography sx={{ wordBreak: "break-word" }}>
              {cache.queryUsed || "-"}
              {source && (
                <Box
                  component="span"
                  sx={{ color: "#94a3b8", ml: 1, fontSize: 13 }}
                >
                  ({source})
                </Box>
              )}
            </Typography>
          </ParamRow>
          <ParamRow label="Обновлено">
            {formatDate(cache.fetchedAt)}
            {cache.cacheAgeHours !== null &&
              cache.cacheAgeHours !== undefined && (
                <Box component="span" sx={{ color: "#94a3b8", ml: 1 }}>
                  ({formatAge(cache.cacheAgeHours)})
                </Box>
              )}
          </ParamRow>
          {cache.isStale && (
            <Typography
              sx={{ color: "#be123c", fontWeight: 600, fontSize: 14 }}
            >
              ⚠️ Кэш устарел, рекомендуется обновить
            </Typography>
          )}
        </Stack>
      </InfoCard>

      <Box className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Paper
          elevation={0}
          className="rounded-2xl border border-slate-200 bg-white p-4"
        >
          <Label>Рыночная цена</Label>
          <Typography sx={{ fontSize: 26, fontWeight: 700, color: "#059669" }}>
            {formatPrice(cache.marketPrice)}
          </Typography>
        </Paper>
        <Paper
          elevation={0}
          className="rounded-2xl border border-slate-200 bg-white p-4"
        >
          <Label>Мин. цена товара</Label>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
            {formatPrice(cache.minMatchPrice)}
          </Typography>
        </Paper>
        <Paper
          elevation={0}
          className="rounded-2xl border border-slate-200 bg-white p-4"
        >
          <Label>Подходит под ТС</Label>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
            {cache.matchCount ?? 0} из {cache.itemsCount ?? 0} товаров
          </Typography>
        </Paper>
        <Paper
          elevation={0}
          className="rounded-2xl border border-slate-200 bg-white p-4"
        >
          <Label>Поставщиков в наличии</Label>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
            {cache.suppliersCount ?? 0} магазинов
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
            ({cache.availableCount ?? 0} шт. в наличии)
          </Typography>
        </Paper>
      </Box>

      <InfoCard title="Диапазон цен всей выдачи">
        <Box className="flex flex-wrap gap-x-6 gap-y-2 rounded-xl bg-slate-50 p-3">
          <Typography>
            Минимум: <b>{formatPrice(cache.priceMin)}</b>
          </Typography>
          <Typography>
            Средняя: <b>{formatPrice(cache.priceAvg)}</b>
          </Typography>
          <Typography>
            Максимум: <b>{formatPrice(cache.priceMax)}</b>
          </Typography>
        </Box>
        {cache.marginNote && (
          <Box className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3">
            <Typography
              sx={{ color: "#be123c", fontWeight: 600, fontSize: 14 }}
            >
              ⚠️ {cache.marginNote}
            </Typography>
          </Box>
        )}
      </InfoCard>
    </Stack>
  );
};

// ── Нижний блок: предложения поставщиков ──
const OffersTable = ({ offers }) => {
  if (!offers?.length) {
    return (
      <EmptyBox>
        По данному запросу подходящих предложений на Satu.kz не обнаружено
      </EmptyBox>
    );
  }

  const sorted = [...offers].sort(
    (a, b) =>
      (OFFER_STATUS[a.status]?.order ?? 9) -
      (OFFER_STATUS[b.status]?.order ?? 9),
  );

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      className="rounded-2xl border border-slate-200"
      sx={{ maxHeight: 600 }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Фото</TableCell>
            <TableCell sx={{ minWidth: 260 }}>Товар</TableCell>
            <TableCell sx={{ minWidth: 170 }}>Статус отбора</TableCell>
            <TableCell align="right">Цена за ед. закупки</TableCell>
            <TableCell align="right">Цена в магазине</TableCell>
            <TableCell>Наличие</TableCell>
            <TableCell>Поставщик</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((offer, i) => {
            const status = OFFER_STATUS[offer.status] || {
              label: offer.status || "-",
              tone: "slate",
            };
            const excluded = offer.status === "excluded";
            return (
              <TableRow
                key={i}
                hover
                sx={{
                  bgcolor: offer.status === "match" ? "#f6fdf9" : undefined,
                  "& td": { color: excluded ? "#94a3b8" : undefined },
                }}
              >
                <TableCell>
                  <Box className="h-12 w-12 overflow-hidden rounded-lg border border-slate-100 bg-white">
                    {offer.imageUrl ? (
                      <a
                        href={offer.imageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={offer.imageUrl}
                          alt={offer.title || ""}
                          className="h-full w-full object-contain"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </a>
                    ) : (
                      <Box className="flex h-full w-full items-center justify-center text-slate-300">
                        <LocalOffer fontSize="small" />
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box className="flex items-start gap-1">
                    <span>{offer.title || "-"}</span>
                    {offer.url && (
                      <IconButton
                        size="small"
                        href={offer.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ p: 0.25 }}
                      >
                        <OpenInNew sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <ToneChip tone={status.tone} label={status.label} />
                  {offer.reason && (
                    <Typography
                      sx={{ fontSize: 12, color: "#94a3b8", mt: 0.5 }}
                    >
                      Причина: {offer.reason}
                    </Typography>
                  )}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {formatPrice(offer.unitPrice)}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {formatPrice(offer.price)}
                  {offer.packSize > 1 && (
                    <Box sx={{ fontSize: 12, color: "#94a3b8" }}>
                      за упак. {offer.packSize} шт
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Box className="flex items-center gap-1.5">
                    <Box
                      className="h-2 w-2 rounded-full"
                      sx={{ bgcolor: offer.available ? "#10b981" : "#cbd5e1" }}
                    />
                    {offer.available ? "В наличии" : "Под заказ / Нет"}
                  </Box>
                </TableCell>
                <TableCell>
                  <div>{offer.supplier || "-"}</div>
                  {offer.supplierRegion && (
                    <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                      {offer.supplierRegion}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const MarketProfileModal = ({ open, onClose, profile }) => {
  const marketProfile = profile?.marketProfile;
  const riskProfile = profile?.riskProfile;
  const marketplaceQuery = marketProfile?.marketplaceQuery;
  const lotId = profile?.lotId ?? profile?.id;

  // ── План ТС и кэш маркетплейса (/api/marketplace/plans) ──
  const [planData, setPlanData] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planRefreshing, setPlanRefreshing] = useState(false);
  const [planError, setPlanError] = useState(null);

  const loadPlan = useCallback(async () => {
    if (!lotId) return;
    setPlanLoading(true);
    setPlanError(null);
    try {
      const res = await fetchWithToken(`/api/marketplace/plans/${lotId}`);
      if (!res.ok) throw new Error(await readApiError(res));
      setPlanData(await res.json());
    } catch (err) {
      setPlanError(err.message);
    } finally {
      setPlanLoading(false);
    }
  }, [lotId]);

  const refreshPlan = async () => {
    if (!lotId) return;
    setPlanRefreshing(true);
    setPlanError(null);
    try {
      const res = await fetchWithToken(
        `/api/marketplace/plans/${lotId}/refresh`,
        {
          method: "POST",
        },
      );
      if (!res.ok) throw new Error(await readApiError(res));
      setPlanData(await res.json());
    } catch (err) {
      setPlanError(err.message);
    } finally {
      setPlanRefreshing(false);
    }
  };

  useEffect(() => {
    if (open) {
      setPlanData(null);
      loadPlan();
    } else {
      setPlanData(null);
      setPlanError(null);
    }
  }, [open, loadPlan]);

  // ── Satu marketplace analysis state ──
  const [endPage, setEndPage] = useState(1);
  const [strictTitle, setStrictTitle] = useState(false);
  const [bestLimit, setBestLimit] = useState(20);
  const [satuLoading, setSatuLoading] = useState(false);
  const [satuData, setSatuData] = useState(null);
  const [satuError, setSatuError] = useState(null);
  const [satuValue, setSatuValue] = useState("");

  const satuAnalyze = () => {
    fetchSatuAnalysis(satuValue, endPage, strictTitle, bestLimit);
  };

  const fetchSatuAnalysis = async (query, endPageVal, strictVal, limitVal) => {
    if (!query) return;
    setSatuValue(query);
    setSatuLoading(true);
    setSatuError(null);
    try {
      const params = buildSatuParams(query, endPageVal, strictVal, limitVal);
      const res = await fetch(`${SATU_API_BASE}?${params}`);
      if (!res.ok) throw new Error(`Ошибка ${res.status}: ${res.statusText}`);
      const json = await res.json();
      setSatuData(json);
    } catch (err) {
      setSatuError(err.message);
    } finally {
      setSatuLoading(false);
    }
  };

  // Auto-run once the plan is loaded: query and strict flag come from /api/marketplace/plans
  useEffect(() => {
    if (!open) {
      setSatuData(null);
      setSatuError(null);
      setSatuValue("");
      return;
    }
    if (!planData && !planError) return;

    const strictVal = Boolean(
      planData?.strictTitleMatch ??
      planData?.marketplaceCache?.strictTitleMatch ??
      planData?.marketplacePlan?.strictTitleMatch,
    );
    const query = planData?.marketplaceCache?.queryUsed || marketplaceQuery;

    setStrictTitle(strictVal);
    setSatuData(null);
    setSatuError(null);
    if (query) fetchSatuAnalysis(query, endPage, strictVal, bestLimit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planData, planError]);

  const handleRefreshSatu = () => {
    const query = satuValue || marketplaceQuery;
    if (query) fetchSatuAnalysis(query, endPage, strictTitle, bestLimit);
  };

  if (!profile) return null;

  const plan = planData?.marketplacePlan;
  const cache = planData?.marketplaceCache;
  const isNonMarketplace = plan && !plan.isMarketplaceItem;
  const loadFailed = !planData && !!planError;

  let fromPct = 0;
  let toPct = 0;
  if (satuData?.price_stats) {
    const { min_price, max_price, normal_range } = satuData.price_stats;
    const range = max_price - min_price || 1;
    fromPct = ((normal_range.from - min_price) / range) * 100;
    toPct = ((normal_range.to - min_price) / range) * 100;
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white shadow-2xl"
        sx={{
          width: "96vw",
          maxWidth: 1800,
          maxHeight: "95vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* HEADER */}
        <Box className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 bg-white px-8 py-5">
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
              План ТС и цены маркетплейса
            </Typography>
            <Typography sx={{ mt: 0.5, color: "#64748b", fontSize: 14 }}>
              Аналитика и правила отбора товаров на Satu.kz
            </Typography>
          </Box>

          <Box className="flex items-center gap-2">
            <Button
              variant="contained"
              startIcon={
                planRefreshing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Refresh />
                )
              }
              onClick={refreshPlan}
              disabled={
                planRefreshing || planLoading || !lotId || isNonMarketplace
              }
            >
              Обновить цены
            </Button>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        {/* SCROLLABLE CONTENT */}
        <Box sx={{ overflowY: "auto", flex: 1, minHeight: 0, p: 3, pb: 5 }}>
          {planError && (
            <Box className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 p-4">
              <Typography sx={{ color: "#be123c", fontSize: 14 }}>
                {planError}
              </Typography>
            </Box>
          )}

          <Box className="mb-5 grid grid-cols-1 gap-6 xl:grid-cols-2">
            {/* ПЛАН ПОИСКА ПО ТС */}
            <SectionShell
              icon={<Assignment />}
              iconClass="bg-blue-100"
              iconColor="#2563eb"
              title="План поиска по ТС"
              subtitle="Правила отбора, сформированные ИИ"
            >
              {planLoading ? (
                <Box className="flex justify-center py-10">
                  <CircularProgress size={28} />
                </Box>
              ) : loadFailed ? (
                <EmptyBox>Нет данных</EmptyBox>
              ) : (
                <PlanColumn plan={plan} />
              )}
            </SectionShell>

            {/* АНАЛИЗ ЦЕН + РИСКИ */}
            <Stack spacing={3} sx={{ minWidth: 0 }}>
              <SectionShell
                icon={<QueryStats />}
                iconClass="bg-emerald-100"
                iconColor="#059669"
                title="Анализ цен и кэш Satu.kz"
                subtitle="Текущая рыночная ситуация"
              >
                {planLoading ? (
                  <Box className="flex justify-center py-10">
                    <CircularProgress size={28} />
                  </Box>
                ) : loadFailed ? (
                  <EmptyBox>Нет данных</EmptyBox>
                ) : (
                  <CacheColumn
                    plan={plan}
                    cache={cache}
                    refreshing={planRefreshing}
                    onRefresh={refreshPlan}
                  />
                )}
              </SectionShell>

              <SectionShell
                icon={<WarningAmber />}
                iconClass="bg-orange-100"
                iconColor="#f97316"
                title="Профиль рисков"
                subtitle="Анализ рисков"
              >
                <InfoCard title="Подтверждённые риски">
                  {riskProfile?.confirmedRisks?.length ? (
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {riskProfile.confirmedRisks.map((risk, index) => (
                        <Chip
                          key={index}
                          label={risk}
                          color="error"
                          variant="outlined"
                          sx={{
                            height: "auto",
                            "& .MuiChip-label": {
                              whiteSpace: "normal",
                              py: 0.5,
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Box className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                      <Typography sx={{ color: "#059669", fontWeight: 700 }}>
                        Риски не обнаружены
                      </Typography>
                    </Box>
                  )}
                </InfoCard>
              </SectionShell>
            </Stack>
          </Box>

          {/* НАЙДЕННЫЕ ТОВАРЫ (ОФФЕРЫ) */}
          {!isNonMarketplace && cache && !planLoading && (
            <SectionShell
              icon={<ShoppingBag />}
              iconClass="bg-violet-100"
              iconColor="#7c3aed"
              title="Найденные товары в магазинах"
              subtitle={`Предложения поставщиков Satu.kz (${cache.offers?.length ?? 0})`}
            >
              <OffersTable offers={cache.offers} />
            </SectionShell>
          )}

          {/* ── MARKETPLACE ANALYSIS (Satu) ── */}
          <Box
            className="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-6"
            sx={{
              boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
            }}
          >
            <Box className="mb-5 flex flex-wrap items-start justify-between gap-4">
              <Box className="flex items-center gap-3">
                <Box
                  className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-100"
                  sx={{ color: "#4f46e5" }}
                >
                  <Insights />
                </Box>

                <Box>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: 24, color: "#0f172a" }}
                  >
                    Marketplace Analysis
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: 14 }}>
                    Цены на Satu.kz по запросу из Market Profile
                  </Typography>
                </Box>
              </Box>

              {/* Editable params + refresh */}
              <Box className="flex flex-wrap items-end gap-2">
                <TextField
                  label="Кон. страница"
                  type="number"
                  size="small"
                  value={endPage}
                  onChange={(e) => setEndPage(Number(e.target.value))}
                  inputProps={{ min: 1, max: 20 }}
                  sx={{ width: 130 }}
                />
                <TextField
                  label="Лимит офферов"
                  type="number"
                  size="small"
                  value={bestLimit}
                  onChange={(e) => setBestLimit(Number(e.target.value))}
                  inputProps={{ min: 1, max: 100 }}
                  sx={{ width: 130 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={strictTitle}
                      onChange={(e) => setStrictTitle(e.target.checked)}
                    />
                  }
                  label="Строгое совпадение"
                  sx={{ mr: 0, color: "#475569", fontSize: 14 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Refresh fontSize="small" />}
                  onClick={handleRefreshSatu}
                  disabled={satuLoading || !(satuValue || marketplaceQuery)}
                >
                  Обновить
                </Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{ margin: 2, display: "flex", gap: 1, alignItems: "stretch" }}
            >
              <TextField
                size="small"
                value={satuValue}
                onChange={(e) => setSatuValue(e.target.value)}
                sx={{ width: "100%" }}
              />

              <Button
                variant="contained"
                onClick={satuAnalyze}
                sx={{ minHeight: "100%" }}
              >
                Найти
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {/* Fixed (non-editable) params, shown for transparency */}
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              sx={{ mb: 3, gap: 1 }}
            >
              <Chip
                size="small"
                variant="outlined"
                label="selected_source: satu"
              />
              <Chip
                size="small"
                variant="outlined"
                label="selected_category: auto"
              />
              <Chip size="small" variant="outlined" label="start_page: 1" />
              <Chip
                size="small"
                variant="outlined"
                label="remove_outliers: false"
              />
            </Stack>
            {!marketplaceQuery && !satuValue && !planLoading && (
              <Typography sx={{ color: "#94a3b8" }}>
                В профиле не найден поисковый запрос (marketplaceQuery)
              </Typography>
            )}
            {satuError && (
              <Box className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4">
                <Typography sx={{ color: "#dc2626", fontSize: 14 }}>
                  {satuError}
                </Typography>
              </Box>
            )}
            {satuLoading && (
              <Box className="flex items-center justify-center py-10">
                <CircularProgress size={28} />
              </Box>
            )}
            {satuData && !satuLoading && satuData.total_found === 0 && (
              <Box className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white py-10 text-center">
                <Inventory2 sx={{ color: "#cbd5e1", fontSize: 32 }} />
                <Typography sx={{ fontWeight: 600, color: "#475569" }}>
                  Совпадений не найдено
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>
                  {satuData.message ||
                    "По данному запросу подходящих товаров на Satu.kz не найдено"}
                </Typography>
              </Box>
            )}
            {satuData && !satuLoading && satuData.total_found > 0 && (
              <Stack spacing={3}>
                {/* Stat cards */}
                <Box className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <StatCard
                    label="Всего найдено"
                    value={satuData.total_found}
                  />
                  <StatCard
                    label="Мин. цена"
                    value={formatPrice(satuData.price_stats?.min_price)}
                    color="#059669"
                  />
                  <StatCard
                    label="Средняя цена"
                    value={formatPrice(satuData.price_stats?.average_price)}
                  />
                  <StatCard
                    label="Макс. цена"
                    value={formatPrice(satuData.price_stats?.max_price)}
                    color="#dc2626"
                  />
                </Box>

                {/* Price range */}
                {satuData.price_stats?.normal_range && (
                  <Paper
                    elevation={0}
                    className="rounded-2xl border border-slate-200 p-5"
                  >
                    <Box className="mb-2 flex items-center justify-between">
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Нормальный диапазон цен
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                        Медиана:{" "}
                        <Box
                          component="span"
                          sx={{ color: "#334155", fontWeight: 700 }}
                        >
                          {formatPrice(satuData.price_stats.median_price)}
                        </Box>
                      </Typography>
                    </Box>
                    <Box className="relative h-2 overflow-hidden rounded-full bg-slate-100">
                      <Box
                        className="absolute top-0 h-full rounded-full bg-indigo-500"
                        style={{
                          left: `${fromPct}%`,
                          width: `${Math.max(toPct - fromPct, 0)}%`,
                        }}
                      />
                    </Box>
                    <Box className="mt-1 flex justify-between text-[11px] text-slate-400">
                      <span>
                        {formatPrice(satuData.price_stats.normal_range.from)}
                      </span>
                      <span>
                        {formatPrice(satuData.price_stats.normal_range.to)}
                      </span>
                    </Box>
                  </Paper>
                )}

                {/* Suppliers */}
                {satuData.suppliers?.length > 0 && (
                  <Paper
                    elevation={0}
                    className="rounded-2xl border border-slate-200 p-5"
                  >
                    <Box className="mb-4 flex items-center gap-2">
                      <Inventory2 fontSize="small" sx={{ color: "#6366f1" }} />
                      <Typography sx={{ fontWeight: 700, color: "#334155" }}>
                        Поставщики ({satuData.suppliers.length})
                      </Typography>
                    </Box>
                    <TableContainer sx={{ maxHeight: 320 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Поставщик</TableCell>
                            <TableCell align="right">Товаров</TableCell>
                            <TableCell align="right">Мин.</TableCell>
                            <TableCell align="right">Средняя</TableCell>
                            <TableCell align="right">Макс.</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {satuData.suppliers.map((s, i) => (
                            <TableRow key={i} hover>
                              <TableCell sx={{ fontWeight: 500 }}>
                                {s.name}
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  size="small"
                                  label={s.products_count}
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ color: "#059669", fontWeight: 600 }}
                              >
                                {formatPrice(s.min_price)}
                              </TableCell>
                              <TableCell align="right">
                                {formatPrice(s.average_price)}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ color: "#94a3b8" }}
                              >
                                {formatPrice(s.max_price)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}

                {/* Best offers */}
                {satuData.best_offers?.length > 0 && (
                  <Box>
                    <Box className="mb-3 flex items-center gap-2">
                      <LocalOffer fontSize="small" sx={{ color: "#6366f1" }} />
                      <Typography sx={{ fontWeight: 700, color: "#334155" }}>
                        Лучшие предложения ({satuData.best_offers.length})
                      </Typography>
                    </Box>
                    <Box className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      {satuData.best_offers.map((offer, i) => (
                        <Box
                          key={i}
                          component="a"
                          href={offer.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <Paper
                            elevation={0}
                            className="flex gap-3 rounded-2xl border border-slate-200 p-3 transition hover:border-indigo-300 hover:shadow-md"
                          >
                            <Box className="h-[72px] w-[72px] flex-shrink-0 overflow-hidden rounded-xl border border-slate-100 bg-white">
                              {offer.image_url ? (
                                <img
                                  src={offer.image_url}
                                  alt={offer.title}
                                  className="h-full w-full object-contain p-1"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <Box className="flex h-full w-full items-center justify-center text-slate-300">
                                  <LocalOffer fontSize="small" />
                                </Box>
                              )}
                            </Box>

                            <Box className="min-w-0 flex-1">
                              <Typography
                                className="line-clamp-2"
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: "#1e293b",
                                  lineHeight: 1.35,
                                }}
                              >
                                {offer.title}
                              </Typography>

                              <Box className="mt-1 flex items-center gap-2">
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: 16,
                                    color: "#0f172a",
                                  }}
                                >
                                  {formatPrice(offer.price)}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={offer.score}
                                  color={scoreColor(offer.score)}
                                />
                              </Box>

                              <Typography
                                sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}
                              >
                                {offer.supplier}
                                {offer.company_region
                                  ? ` · ${offer.company_region}`
                                  : ""}
                                {offer.available ? " · в наличии" : ""}
                              </Typography>

                              {offer.reason && (
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    color: "#818cf8",
                                    fontStyle: "italic",
                                    mt: 0.5,
                                  }}
                                >
                                  {offer.reason}
                                </Typography>
                              )}
                            </Box>
                          </Paper>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {satuData.removed_as_outliers > 0 && (
                  <Typography
                    sx={{ textAlign: "center", fontSize: 12, color: "#94a3b8" }}
                  >
                    Убрано как выбросы: <b>{satuData.removed_as_outliers}</b> из{" "}
                    {satuData.total_before_outlier_filter}
                  </Typography>
                )}
              </Stack>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default MarketProfileModal;
